import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Loader2, AlertTriangle, AlertCircle, CheckCircle2, RefreshCw, Trash2, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { auditTemplateNoticeContradictions, removeLegacyNoticeTerm } from '../api';

/**
 * Notice-Contradiction Audit dialog.
 *
 * Renders the JSON from `GET /api/offer-letters/templates/notice-contradiction-audit`.
 * Unlike the lighter Notice-Period Audit (which checks only the
 * `terms_and_conditions.notice_period` field), this audit scans EVERY entry
 * in each template's `pdf_terms_list` + `clauses` overrides looking for
 * content that contradicts the new tiered Termination & Recovery clause.
 *
 * Per-severity colour coding:
 *   - high     → red — matches KNOWN_LEGACY_NOTICE_PHRASES list, eligible
 *                for one-click cleanup
 *   - suspect  → amber — heuristic match (notice + duration), needs HR review
 *   - clean    → green
 */
const SEVERITY_META = {
  high: {
    label: 'High',
    icon: AlertTriangle,
    chipClass: 'bg-red-100 text-red-700 border-red-300',
    rowClass: 'border-l-4 border-red-400 bg-red-50/40',
  },
  suspect: {
    label: 'Suspect',
    icon: AlertCircle,
    chipClass: 'bg-amber-100 text-amber-700 border-amber-300',
    rowClass: 'border-l-4 border-amber-400 bg-amber-50/40',
  },
  clean: {
    label: 'Clean',
    icon: CheckCircle2,
    chipClass: 'bg-emerald-100 text-emerald-700 border-emerald-300',
    rowClass: 'border-l-4 border-emerald-400 bg-emerald-50/30',
  },
};

const NoticeContradictionAuditDialog = ({ open, onOpenChange, onCleanupDone }) => {
  const [loading, setLoading] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [report, setReport] = useState(null);
  const [filter, setFilter] = useState('all');
  const [expanded, setExpanded] = useState({});

  const fetchAudit = async () => {
    setLoading(true);
    try {
      const res = await auditTemplateNoticeContradictions();
      setReport(res.data);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Failed to load contradiction audit');
    } finally {
      setLoading(false);
    }
  };

  // Auto-fetch when the dialog opens so HR always sees fresh data.
  useEffect(() => {
    if (open) {
      fetchAudit();
      setFilter('all');
      setExpanded({});
    }
  }, [open]);

  const handleCleanup = async () => {
    if (!window.confirm(
      'This will scrub ALL known-legacy notice-period phrases from every template\'s ' +
      'pdf_terms_list and clauses overrides. This action is idempotent and safe to ' +
      're-run.\n\nContinue?'
    )) return;
    setCleaning(true);
    try {
      const res = await removeLegacyNoticeTerm();
      const data = res.data || {};
      toast.success(
        `Cleanup complete — ${data.modified_count ?? 0} templates updated ` +
        `(${data.total_terms_removed ?? 0} terms + ${data.total_clauses_overrides_removed ?? 0} clauses scrubbed).`
      );
      await fetchAudit();
      if (typeof onCleanupDone === 'function') onCleanupDone();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Cleanup failed');
    } finally {
      setCleaning(false);
    }
  };

  const summary = report?.summary;
  const visibleRows = !report ? [] : (
    filter === 'all'
      ? report.templates
      : report.templates.filter(r => r.severity === filter)
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
        data-testid="notice-contradiction-audit-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle size={20} className="text-red-600" />
            Notice-Contradiction Audit
            <Button
              variant="ghost"
              size="sm"
              className="ml-auto h-7 px-2 text-xs"
              onClick={fetchAudit}
              disabled={loading}
              data-testid="contradiction-audit-refresh-btn"
            >
              <RefreshCw size={12} className={`mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Scans every template&apos;s <code className="text-[10px] bg-slate-100 px-1 py-0.5 rounded">pdf_terms_list</code> and <code className="text-[10px] bg-slate-100 px-1 py-0.5 rounded">clauses</code> overrides for content that contradicts the new tiered Termination &amp; Recovery clause.
          </DialogDescription>
        </DialogHeader>

        {loading && !report && (
          <div className="flex-1 flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-slate-400" size={32} />
          </div>
        )}

        {report && (
          <>
            {/* Policy reference banner */}
            <div className="rounded-lg border border-indigo-200 bg-indigo-50/60 p-3 text-xs text-indigo-900">
              <p className="font-semibold mb-1">{report.policy_reference?.clause_title}</p>
              <div className="grid grid-cols-2 gap-2">
                <div><strong>Tenured:</strong> {report.policy_reference?.tenured_tier}</div>
                <div><strong>Probation:</strong> {report.policy_reference?.probation_tier}</div>
              </div>
            </div>

            {/* Summary chips (filter buttons) */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {[
                ['all', `Total ${summary.total}`, 'bg-slate-100 text-slate-700 border-slate-300'],
                ['high', `High ${summary.high_count}`, SEVERITY_META.high.chipClass],
                ['suspect', `Suspect ${summary.suspect_count}`, SEVERITY_META.suspect.chipClass],
                ['clean', `Clean ${summary.clean_count}`, SEVERITY_META.clean.chipClass],
              ].map(([key, label, cls]) => (
                <button
                  key={key}
                  type="button"
                  className={`px-3 py-1.5 rounded border text-xs font-medium transition ${cls} ${filter === key ? 'ring-2 ring-offset-1 ring-indigo-400' : 'opacity-80 hover:opacity-100'}`}
                  onClick={() => setFilter(key)}
                  data-testid={`contradiction-audit-filter-${key}`}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* One-click cleanup CTA — only shown when high-severity items exist */}
            {summary.high_count > 0 && (
              <div className="rounded-md border border-red-200 bg-red-50/50 p-3 flex items-center gap-3">
                <Trash2 size={20} className="text-red-600 flex-shrink-0" />
                <div className="flex-1 text-xs text-red-900">
                  <div className="font-semibold mb-0.5">
                    {summary.high_count} template{summary.high_count === 1 ? '' : 's'} contain known-legacy notice phrases.
                  </div>
                  <div className="text-red-700">
                    Run the cleanup endpoint to scrub all matched phrases from <code>pdf_terms_list</code> + <code>clauses</code> overrides. Idempotent and safe to re-run.
                  </div>
                </div>
                <Button
                  variant="default"
                  size="sm"
                  className="bg-red-600 hover:bg-red-700 text-white"
                  onClick={handleCleanup}
                  disabled={cleaning}
                  data-testid="contradiction-audit-cleanup-btn"
                >
                  {cleaning ? (
                    <Loader2 size={14} className="mr-1.5 animate-spin" />
                  ) : (
                    <Trash2 size={14} className="mr-1.5" />
                  )}
                  Run Cleanup
                </Button>
              </div>
            )}

            {/* Results list */}
            <div className="flex-1 overflow-y-auto space-y-2 pr-1">
              {visibleRows.length === 0 ? (
                <div className="py-12 text-center text-sm text-slate-400">
                  No templates match the current filter.
                </div>
              ) : (
                visibleRows.map((row) => {
                  const meta = SEVERITY_META[row.severity] || SEVERITY_META.suspect;
                  const Icon = meta.icon;
                  const hasFindings = (row.findings || []).length > 0;
                  const isOpen = !!expanded[row.id];
                  return (
                    <div
                      key={row.id}
                      className={`rounded-md px-3 py-2.5 ${meta.rowClass}`}
                      data-testid={`contradiction-audit-row-${row.id}`}
                    >
                      <button
                        type="button"
                        onClick={() => hasFindings && setExpanded(prev => ({ ...prev, [row.id]: !prev[row.id] }))}
                        className={`w-full flex items-start gap-2 text-left ${hasFindings ? 'cursor-pointer' : 'cursor-default'}`}
                        data-testid={`contradiction-audit-row-toggle-${row.id}`}
                      >
                        <Icon size={16} className="mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <span className="font-medium text-sm text-slate-900 truncate">{row.name || '—'}</span>
                            <Badge className={`text-[10px] py-0 px-1.5 ${meta.chipClass}`}>
                              {meta.label}
                            </Badge>
                            {row.category && (
                              <span className="text-[11px] text-slate-500">· {row.category}</span>
                            )}
                            {hasFindings && (
                              <span className="text-[11px] text-slate-500 ml-auto">
                                {row.findings.length} finding{row.findings.length === 1 ? '' : 's'}
                              </span>
                            )}
                          </div>
                          {!hasFindings && (
                            <p className="text-[11px] text-slate-600">No contradictions detected.</p>
                          )}
                        </div>
                        {hasFindings && (
                          <ChevronRight
                            size={14}
                            className={`mt-1 text-slate-400 transition-transform ${isOpen ? 'rotate-90' : ''}`}
                          />
                        )}
                      </button>

                      {hasFindings && isOpen && (
                        <div className="mt-2 ml-6 space-y-2">
                          {row.findings.map((f, i) => {
                            const fMeta = SEVERITY_META[f.severity] || SEVERITY_META.suspect;
                            return (
                              <div
                                key={i}
                                className="rounded border border-slate-200 bg-white p-2.5 text-xs"
                                data-testid={`contradiction-audit-finding-${row.id}-${i}`}
                              >
                                <div className="flex flex-wrap items-center gap-2 mb-1.5">
                                  <Badge className={`text-[10px] py-0 px-1.5 ${fMeta.chipClass}`}>
                                    {fMeta.label}
                                  </Badge>
                                  <code className="text-[10px] bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">
                                    {f.source} → {f.location}
                                  </code>
                                </div>
                                <p className="text-slate-800 leading-relaxed mb-1.5 whitespace-pre-wrap break-words">
                                  &ldquo;{f.text}&rdquo;
                                </p>
                                <p className="text-[11px] text-slate-500 italic leading-relaxed">
                                  {f.reason}
                                </p>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </>
        )}

        <DialogFooter className="mt-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="contradiction-audit-close-btn"
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NoticeContradictionAuditDialog;
